import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import AddGameToReview from '../components/AddGameToReview';

const GameReviews = () => {
    const { user, isAuthenticated } = useContext(AuthContext);
    const [recommendations, setRecommendations] = useState([]);
    const [featuredReviews, setFeaturedReviews] = useState([]);
    const [recentReviews, setRecentReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('featured');
    const [showAddGameModal, setShowAddGameModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedReviews, setExpandedReviews] = useState({});
    const reviewsPerPage = 5;

    useEffect(() => {
        fetchData();
    }, [user]);

    useEffect(() => {
        setCurrentPage(1);
        setExpandedReviews({});
    }, [activeTab]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch featured reviews
            const featuredRes = await axios.get('/api/reviews/featured?limit=5');
            setFeaturedReviews(featuredRes.data.reviews || []);

            // Fetch recommendations if logged in
            if (isAuthenticated && user) {
                const recRes = await axios.get(`/api/reviews/recommendations/${user._id}?limit=6`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                setRecommendations(recRes.data.recommendations || []);
            }

            // Fetch recent reviews from all games
            const recentRes = await axios.get('/api/reviews/target/Game/all');
            setRecentReviews(recentRes.data || []);

        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating) => {
        return (
            <div className="star-rating-display">
                {[1, 2, 3, 4, 5].map((star) => (
                    <span
                        key={star}
                        className={`star ${star <= rating ? 'filled' : 'empty'}`}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    const renderCategoryRatings = (ratings, compact = false) => {
        if (!ratings) return null;
        
        const categories = [
            { key: 'graphics', label: '🎨', fullLabel: 'Graphics', color: '#FF6B6B' },
            { key: 'gameplay', label: '🎮', fullLabel: 'Gameplay', color: '#4ECDC4' },
            { key: 'story', label: '📖', fullLabel: 'Story', color: '#95E1D3' },
            { key: 'sound', label: '🔊', fullLabel: 'Sound', color: '#F38181' },
            { key: 'value', label: '💰', fullLabel: 'Value', color: '#FFA07A' },
            { key: 'replayability', label: '🔄', fullLabel: 'Replayability', color: '#AA96DA' }
        ];

        const filledCategories = categories.filter(cat => ratings[cat.key] && ratings[cat.key] > 0);
        
        if (filledCategories.length === 0) return null;

        if (compact) {
            return (
                <div style={{ 
                    marginTop: '8px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                }}>
                    {filledCategories.map(({ key, label, fullLabel, color }) => (
                        <div key={key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            fontSize: '0.85rem'
                        }}>
                            <span title={fullLabel}>{label}</span>
                            <span style={{ color: color, fontWeight: 'bold' }}>{ratings[key]}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div style={{ 
                marginTop: '12px', 
                padding: '12px',
                backgroundColor: 'rgba(0,0,0,0.15)',
                borderRadius: '6px',
                border: '1px solid var(--border)'
            }}>
                <h4 style={{ marginBottom: '8px', fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                    📊 Detailed Ratings
                </h4>
                <div style={{ display: 'grid', gap: '6px' }}>
                    {filledCategories.map(({ key, label, fullLabel, color }) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ minWidth: '120px', fontSize: '0.85rem' }}>{label} {fullLabel}</span>
                            <div style={{ flex: 1, display: 'flex', gap: '2px' }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        style={{
                                            color: star <= ratings[key] ? color : 'var(--text-tertiary)',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        ★
                                    </span>
                                ))}
                            </div>
                            <span style={{ 
                                minWidth: '28px', 
                                textAlign: 'right', 
                                fontWeight: 'bold',
                                color: color,
                                fontSize: '0.85rem'
                            }}>
                                {ratings[key]}/5
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const toggleReviewExpansion = (reviewId) => {
        setExpandedReviews(prev => ({
            ...prev,
            [reviewId]: !prev[reviewId]
        }));
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this review?')) {
            return;
        }

        try {
            await axios.delete(`/api/reviews/${reviewId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            // Refresh the reviews
            fetchData();
            alert('Review deleted successfully!');
        } catch (error) {
            console.error('Error deleting review:', error);
            alert(error.response?.data?.message || 'Failed to delete review');
        }
    };

    const handleEditReview = (review) => {
        // Store review data in localStorage for the edit form to access
        localStorage.setItem('editingReview', JSON.stringify({
            reviewId: review._id,
            targetType: review.targetType,
            targetId: review.targetId._id,
            targetName: review.targetId.name,
            rating: review.rating,
            comment: review.comment,
            ratings: review.ratings,
            pros: review.pros,
            cons: review.cons
        }));
        
        // Open the add game modal which will detect the editing review
        setShowAddGameModal(true);
    };

    const renderPagination = (totalItems) => {
        const totalPages = Math.ceil(totalItems / reviewsPerPage);
        if (totalPages <= 1) return null;

        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
                marginBottom: '16px'
            }}>
                <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage === 1 ? 'var(--input-bg)' : 'var(--btn-primary)',
                        color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-light)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontWeight: '600'
                    }}
                >
                    ← Previous
                </button>
                
                <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                    Page {currentPage} of {totalPages}
                </span>

                <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: currentPage === totalPages ? 'var(--input-bg)' : 'var(--btn-primary)',
                        color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text-light)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontWeight: '600'
                    }}
                >
                    Next →
                </button>
            </div>
        );
    };

    const renderCompactReview = (review, featured = false) => {
        const isExpanded = expandedReviews[review._id];
        const maxCommentLength = 150;
        const needsTruncation = review.comment && review.comment.length > maxCommentLength;
        const displayComment = needsTruncation && !isExpanded 
            ? review.comment.substring(0, maxCommentLength) + '...' 
            : review.comment;

        return (
            <div
                key={review._id}
                className="card review-card"
                style={{
                    border: featured ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                }}
            >
                <div className="card-body-content">
                    {/* Game Banner as Card Header */}
                    {review.targetId?.bannerImage && (
                        <div className="card-header-image" style={{ width: '100%', height: '120px', marginBottom: '16px', borderRadius: '12px', overflow: 'hidden' }}>
                            <Link to={`/game/${review.targetId._id}`} style={{ display: 'block', textDecoration: 'none' }}>
                                <img 
                                    src={review.targetId.bannerImage} 
                                    alt={review.targetId.name}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                    }}
                                />
                            </Link>
                        </div>
                    )}

                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        {featured && (
                            <span className="badge" style={{
                                backgroundColor: 'var(--accent)',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}>
                                ⭐ FEATURED
                            </span>
                        )}
                        {review.verifiedOwnership && (
                            <span className="badge" style={{
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                color: '#10b981',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}>
                                ✓ Verified
                            </span>
                        )}
                    </div>

                    {/* Game Title and Username */}
                    <div style={{ marginBottom: '16px' }}>
                        <h3 className="card-title" style={{ marginBottom: '8px' }}>
                            {review.targetId?.name || 'Game'}
                        </h3>
                        <p className="card-description" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                            Reviewed by{' '}
                            <Link 
                                to={`/profile/${review.user?.username}`}
                                style={{ 
                                    color: 'var(--primary)', 
                                    fontWeight: '600',
                                    textDecoration: 'none'
                                }}
                            >
                                {review.user?.username || 'Anonymous'}
                            </Link>
                            {' · '}
                            {new Date(review.createdAt).toLocaleDateString()}
                        </p>
                    </div>

                    {/* Rating */}
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {renderStars(review.rating)}
                            <span style={{ fontWeight: '600', color: 'var(--text-accent)', fontSize: '16px' }}>
                                {review.rating}/5
                            </span>
                        </div>
                        {!isExpanded && renderCategoryRatings(review.ratings, true)}
                    </div>

                    {/* Comment - Truncated */}
                    <div style={{ marginBottom: '16px', flex: 1 }}>
                        <p style={{ 
                            color: 'var(--text-primary)', 
                            lineHeight: '1.6',
                            fontSize: '14px'
                        }}>
                            {displayComment}
                        </p>
                    </div>

                    {/* Actions */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        marginTop: 'auto',
                        paddingTop: '16px',
                        borderTop: '1px solid var(--border-color)',
                        flexWrap: 'wrap'
                    }}>
                        {needsTruncation && (
                            <button
                                onClick={() => toggleReviewExpansion(review._id)}
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                            >
                                {isExpanded ? '📖 Show Less' : '📖 Read More'}
                            </button>
                        )}

                        {user && review.user?._id === user._id && (
                            <>
                                <button
                                    onClick={() => handleEditReview(review)}
                                    className="btn btn-secondary"
                                    style={{ flex: needsTruncation ? 'none' : 1 }}
                                >
                                    ✏️ Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteReview(review._id)}
                                    className="btn btn-danger"
                                    style={{ flex: needsTruncation ? 'none' : 1 }}
                                >
                                    🗑️ Delete
                                </button>
                            </>
                        )}
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                            {/* Detailed Ratings */}
                            {renderCategoryRatings(review.ratings, false)}

                            {/* Pros/Cons */}
                            {((review.pros && review.pros.length > 0) || (review.cons && review.cons.length > 0)) && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                    {review.pros && review.pros.length > 0 && (
                                        <div>
                                            <h4 style={{ marginBottom: '12px', fontSize: '14px', color: '#10b981' }}>✅ Pros</h4>
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                {review.pros.map((pro, idx) => (
                                                    <li key={idx} style={{ 
                                                        padding: '6px 10px', 
                                                        marginBottom: '6px', 
                                                        background: 'rgba(16, 185, 129, 0.1)',
                                                        borderRadius: '6px',
                                                        fontSize: '13px'
                                                    }}>
                                                        • {pro}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {review.cons && review.cons.length > 0 && (
                                        <div>
                                            <h4 style={{ marginBottom: '12px', fontSize: '14px', color: '#ef4444' }}>❌ Cons</h4>
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                {review.cons.map((con, idx) => (
                                                    <li key={idx} style={{ 
                                                        padding: '6px 10px', 
                                                        marginBottom: '6px', 
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        borderRadius: '6px',
                                                        fontSize: '13px'
                                                    }}>
                                                        • {con}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Store Links */}
                            {review.targetId?.storeLinks && Object.keys(review.targetId.storeLinks).filter(k => review.targetId.storeLinks[k] && k !== 'other').length > 0 && (
                                <div style={{ 
                                    marginTop: '16px', 
                                    padding: '12px',
                                    backgroundColor: 'var(--card-bg-secondary)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                    alignItems: 'center'
                                }}>
                                    <strong style={{ fontSize: '14px' }}>🛒 Buy:</strong>
                                    {review.targetId.storeLinks.steam && (
                                        <a href={review.targetId.storeLinks.steam} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#1b2838',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            Steam
                                        </a>
                                    )}
                                    {review.targetId.storeLinks.epicGames && (
                                        <a href={review.targetId.storeLinks.epicGames} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#313131',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            Epic
                                        </a>
                                    )}
                                    {review.targetId.storeLinks.gog && (
                                        <a href={review.targetId.storeLinks.gog} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#86328a',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            GOG
                                        </a>
                                    )}
                                    {review.targetId.storeLinks.playstation && (
                                        <a href={review.targetId.storeLinks.playstation} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#003087',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            PS
                                        </a>
                                    )}
                                    {review.targetId.storeLinks.xbox && (
                                        <a href={review.targetId.storeLinks.xbox} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#107c10',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            Xbox
                                        </a>
                                    )}
                                    {review.targetId.storeLinks.nintendo && (
                                        <a href={review.targetId.storeLinks.nintendo} target="_blank" rel="noopener noreferrer"
                                            style={{ 
                                                padding: '6px 12px', 
                                                backgroundColor: '#e60012',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}>
                                            Switch
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Start of main render
    if (loading) {
        return (
            <div className="container" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <div className="spinner"></div>
                <p>Loading game reviews...</p>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '40px 20px' }}>
            <div className="page-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1>🎮 Game Reviews Hub</h1>
                    <p style={{ color: 'var(--text-secondary)', marginTop: '10px' }}>
                        Review ANY game and discover your next favorite
                    </p>
                </div>
                {isAuthenticated && (
                    <button
                        onClick={() => setShowAddGameModal(true)}
                        className="btn btn-primary"
                        style={{ padding: '12px 24px', fontSize: '1rem', fontWeight: 'bold' }}
                    >
                        ✍️ Write Review
                    </button>
                )}
            </div>

            {/* Add Game Modal */}
            {showAddGameModal && (
                <div 
                    className="modal-overlay"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        overflow: 'auto'
                    }}
                    onClick={(e) => {
                        if (e.target.className === 'modal-overlay') {
                            setShowAddGameModal(false);
                        }
                    }}
                >
                    <div 
                        className="modal-content"
                        style={{
                            backgroundColor: 'var(--bg-primary)',
                            borderRadius: '16px',
                            maxWidth: '900px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            position: 'relative',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                            border: '1px solid var(--border)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowAddGameModal(false)}
                            className="modal-close-btn"
                            style={{
                                position: 'sticky',
                                top: '16px',
                                right: '16px',
                                float: 'right',
                                background: 'var(--card-bg)',
                                border: '1px solid var(--border)',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                color: 'var(--text)',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                fontWeight: 'bold'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--error-bg)';
                                e.currentTarget.style.color = 'var(--error-text)';
                                e.currentTarget.style.transform = 'rotate(90deg)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                                e.currentTarget.style.color = 'var(--text)';
                                e.currentTarget.style.transform = 'rotate(0deg)';
                            }}
                        >
                            ✕
                        </button>
                        <div style={{ clear: 'both' }}>
                            <AddGameToReview onClose={() => {
                                setShowAddGameModal(false);
                                fetchData(); // Refresh data after adding review
                            }} />
                        </div>
                    </div>
                </div>
            )}

            {/* Recommendations Section */}
            {isAuthenticated && recommendations.length > 0 && (
                <section style={{ marginBottom: '40px' }}>
                    <h2 style={{ marginBottom: '20px' }}>✨ Recommended For You</h2>
                    <div className="recommendations-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: '20px'
                    }}>
                        {recommendations.map((rec, idx) => (
                            <Link
                                key={idx}
                                to={`/indie-games`}
                                className="recommendation-card"
                                style={{
                                    padding: '20px',
                                    backgroundColor: 'var(--card-bg)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border)',
                                    textDecoration: 'none',
                                    color: 'var(--text)',
                                    transition: 'transform 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <h3 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>
                                    {rec.game?.name || 'Game'}
                                </h3>
                                <div style={{ marginBottom: '10px' }}>
                                    {renderStars(Math.round(rec.predictedRating || rec.game?.averageRating || 0))}
                                    <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                                        {(rec.predictedRating || rec.game?.averageRating || 0).toFixed(1)}/5
                                    </span>
                                </div>
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                    {rec.reason}
                                </p>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    backgroundColor: 'var(--accent)',
                                    color: 'white',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem'
                                }}>
                                    {rec.confidence}% match
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Tabs */}
            <div className="tabs" style={{ marginBottom: '30px', borderBottom: '2px solid var(--border)' }}>
                <button
                    onClick={() => setActiveTab('featured')}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'featured' ? '3px solid var(--accent)' : '3px solid transparent',
                        color: activeTab === 'featured' ? 'var(--accent)' : 'var(--text)',
                        fontWeight: activeTab === 'featured' ? 'bold' : 'normal',
                        cursor: 'pointer'
                    }}
                >
                    ⭐ Featured Reviews
                </button>
                <button
                    onClick={() => setActiveTab('recent')}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'recent' ? '3px solid var(--accent)' : '3px solid transparent',
                        color: activeTab === 'recent' ? 'var(--accent)' : 'var(--text)',
                        fontWeight: activeTab === 'recent' ? 'bold' : 'normal',
                        cursor: 'pointer'
                    }}
                >
                    🕒 Recent Reviews
                </button>
            </div>

            {/* Featured Reviews */}
            {activeTab === 'featured' && (
                <section>
                    {featuredReviews.length === 0 ? (
                        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
                            No featured reviews yet. Check back soon!
                        </p>
                    ) : (
                        <>
                            <div className="reviews-grid">
                                {featuredReviews
                                    .slice((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage)
                                    .map((review) => renderCompactReview(review, true))}
                            </div>
                            {renderPagination(featuredReviews.length)}
                        </>
                    )}
                </section>
            )}

            {/* Recent Reviews */}
            {activeTab === 'recent' && (
                <section>
                    {recentReviews.length === 0 ? (
                        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
                            No reviews yet. Write a review to get started!
                        </p>
                    ) : (
                        <>
                            <div className="reviews-grid">
                                {recentReviews
                                    .slice((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage)
                                    .map((review) => renderCompactReview(review, false))}
                            </div>
                            {renderPagination(recentReviews.length)}
                        </>
                    )}
                </section>
            )}

            {/* CTA Section */}
            <div style={{
                marginTop: '60px',
                padding: '40px',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid var(--border)'
            }}>
                <h2>Ready to share your gaming experience?</h2>
                <p style={{ marginTop: '12px', marginBottom: '24px', color: 'var(--text-secondary)' }}>
                    Write reviews, earn points, and help others discover great games!
                </p>
                {isAuthenticated ? (
                    <button
                        onClick={() => setShowAddGameModal(true)}
                        className="btn btn-primary"
                        style={{
                            display: 'inline-block',
                            padding: '12px 32px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        ✍️ Write Your First Review
                    </button>
                ) : (
                    <Link
                        to="/login"
                        className="btn btn-primary"
                        style={{
                            display: 'inline-block',
                            padding: '12px 32px',
                            textDecoration: 'none'
                        }}
                    >
                        Login to Start Reviewing
                    </Link>
                )}
            </div>
        </div>
    );
};

export default GameReviews;
